import {
  PROJECT_TEMPLATE_DESCRIPTION_MAX,
  PROJECT_TEMPLATE_NAME_MAX,
  projectTemplateInputSchema,
  type ProjectTemplateInput,
} from '@fluxus/shared';
import { useId, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

type Props = {
  initial: { name: string; description: string | null };
  submitLabel: string;
  onSubmit: (input: ProjectTemplateInput) => Promise<unknown>;
  onCancel: () => void;
  onBusyChange?: (busy: boolean) => void;
};

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

/** Nome e descrição do modelo de projeto (salvar um projeto como modelo e renomear; T-204b, DEC-070). */
export function TemplateForm({ initial, submitLabel, onSubmit, onCancel, onBusyChange }: Props) {
  const [name, setName] = useState(initial.name);
  const [description, setDescription] = useState(initial.description ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), description: useId() };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = projectTemplateInputSchema.safeParse({ name, description });
    if (!parsed.success) {
      setErrors(zodFieldErrors(parsed.error));
      return;
    }
    setErrors({});
    setSaving(true);
    onBusyChange?.(true);
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      const fields = apiFieldErrors(err);
      // Nome repetido é do campo nome (a API responde 409 sem campo).
      if (err instanceof ApiError && err.code === 'NAME_TAKEN') setErrors({ name: err.message });
      else if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
      onBusyChange?.(false);
    }
  }

  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-5">
      <FormField id={ids.name} label="Nome do modelo" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={PROJECT_TEMPLATE_NAME_MAX}
          placeholder="Ex.: BSC padrão de indústria"
          onChange={(e) => {
            setName(e.target.value);
            setErrors((x) => ({ ...x, name: undefined }));
          }}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          maxLength={PROJECT_TEMPLATE_DESCRIPTION_MAX}
          rows={3}
          placeholder="Para que tipo de empresa o modelo serve"
          onChange={(e) => {
            setDescription(e.target.value);
            setErrors((x) => ({ ...x, description: undefined }));
          }}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
